// src/features/hector-agent-casediscovery-v3/constants/toolLabels.ts

import React from 'react';
import {
  Scale,
  FilePlus2,
  Files,
  FileText,
  FileSearch,
  BookOpen,
  Book,
  Landmark,
  Globe,
  FolderTree,
  FolderOpen,
  FolderPlus,
  Zap,
  Sparkles,
  Search,
  ScanSearch,
  Bot,
  Microscope,
  Type,
  Edit3,
  Highlighter,
  Navigation,
  MessageSquarePlus,
  Brain,
  MessagesSquare,
  MessageSquare,
  ListChecks,
  PanelRight,
  Mail,
  MailOpen,
  Paperclip,
  Reply,
  Scissors,
} from 'lucide-react';

// ============================================================================
// Backend Tool Labels (single source of truth)
// ============================================================================

export const BACKEND_TOOL_LABELS: Record<string, string> = {
  // Research tools
  search_jurisprudence: 'Recherche de jurisprudence',
  search_case_documents: 'Recherche dans le dossier',
  find_case_documents: 'Recherche de pièce',
  search_doctrine: 'Recherche de doctrine',
  // Légifrance tools
  legifrance_search_code: 'Recherche dans les codes',
  legifrance_search_legislation: 'Recherche de législation',
  legifrance_get_article: "Lecture d'article de loi",
  legifrance_list_codes: 'Liste des codes',
  legifrance_code_toc: 'Sommaire de code',
  // JP verification tool (inclut déjà le fullText — un seul appel)
  search_jp_by_id: 'Vérification de jurisprudence',
  // Document tools
  read_document: 'Lecture de document',
  split_document: 'Découpage du PDF',
  read_attached_document: 'Lecture de pièce jointe',
  // Web tools
  web_search: 'Recherche web',
  web_fetch: 'Lecture de page web',
  // Case tools
  get_case_structure: 'Exploration du dossier',
  // Case Wiki read tools (PR1) — libellés alignés sur le backend (ToolOrchestrator).
  // Aucun nom technique exposé : lecture et survol partagent « Analyse du dossier ».
  get_case_wiki_overview: 'Analyse du dossier',
  read_case_wiki: 'Analyse du dossier',
  launch_case_wiki_analysis: "Lancement de l'analyse avancée",
  create_case: 'Création de dossier',
  get_case_metadata: 'Métadonnées du dossier',
  update_case_metadata: 'Correction du dossier',
  set_piece_numbers: 'Numérotation des pièces',
  create_case_folders: 'Création de dossiers',
  move_pieces: 'Déplacement de pièces',
  copy_pieces: 'Copie de pièces vers un autre dossier',
  rename_pieces: 'Renommage de pièces',
  rename_folders: 'Renommage de dossiers',
  save_document: 'Enregistrement du document',
  // Les conversations enregistrées avant `save_document` rejouent ces deux noms.
  save_canvas_to_case: 'Enregistrement dans le dossier',
  save_attachment_to_case: 'Enregistrement de la pièce jointe dans le dossier',
  compare_documents: 'Comparaison de deux versions',
  read_document_format: 'Lecture de la mise en forme',
  search_cases: 'Recherche de dossier',
  add_related_case: 'Ajout d\'un dossier à la conversation',
  remove_related_case: 'Retrait d\'un dossier de la conversation',
  // System tools
  load_skill: 'Chargement de compétence',
  extract_legal_references: 'Extraction des références juridiques',
  context_compaction: 'Synthétisation de la discussion',
  open_objective: "Ouverture de l'objectif",
  close_objective: "Vérification de l'objectif",
  // Memory & conversation-history tools (agent mémoire persistante, PR #347).
  // Sans mapping, la timeline tombait sur le fallback (nom technique brut, ex
  // « read_memory »). Libellés sans nom technique, cohérents avec le backend
  // (llm/ToolDefinitions.ts) — garder synchronisés avec la SSOT add-in Édition v3.
  read_memory: 'Lecture de la mémoire',
  update_memory: 'Mise à jour de la mémoire',
  list_case_conversations: 'Liste des conversations',
  get_conversation: "Lecture d'une conversation",
  list_quick_actions: 'Consultation des actions rapides',
  manage_quick_actions: 'Gestion des actions rapides',
  propose_ui_actions: 'Actions proposées',
  // Connecteur courrier Microsoft (mailbox_*, lot C) — libellés alignés sur le
  // backend (ToolOrchestrator.getToolDisplayName, hector-agent-case-discovery-v3).
  mailbox_search_messages: 'Recherche dans le courrier',
  mailbox_get_message: "Lecture d'un message",
  mailbox_read_attachment: "Lecture d'une pièce jointe (courrier)",
  mailbox_create_reply_draft: "Préparation d'un brouillon de réponse",
  mailbox_update_reply_draft: 'Modification du brouillon de réponse',
  // Word action tools (agent Édition v3 / add-in Word). Restaurés ici quand une
  // conversation menée dans Word est rouverte dans le workspace : leurs tool_results
  // sont persistés comme n'importe quel autre tool. Sans mapping, ils tombaient sur
  // le fallback (icône + nom technique brut) — d'où l'affichage incohérent corrigé
  // pour l'issue #223. Libellés alignés sur la SSOT add-in
  // (apps/addin-word/.../constants/wordActionLabels.ts) — garder synchronisés.
  get_all_paragraphs: 'Lecture du document',
  get_context_range: 'Lecture de paragraphes',
  get_selection: 'Lecture de la sélection',
  get_document_structure: 'Analyse de la structure',
  get_paragraph_format: 'Lecture du format',
  search_replace: 'Remplacement de texte',
  insert_paragraph: 'Insertion de paragraphe',
  delete_paragraph: 'Suppression de paragraphe',
  apply_text_format: 'Application du formatage',
  apply_font: 'Application de la police',
  clear_formatting: 'Suppression du formatage',
  set_alignment: 'Alignement du texte',
  set_paragraph_style: 'Application du style',
  setup_hector_styles: 'Configuration des styles',
  highlight_range: 'Surlignage de passage',
  navigate_to_paragraph: 'Navigation dans le document',
  insert_comment: 'Annotation du document',
  // Rédaction web (cascade orchestrator-driven + canvas SuperDoc).
  // Sans mapping, ces tools tombaient sur le fallback nom technique brut
  // (« canvas_read »). Les anciens canvas tools par index ne sont plus exposés.
  list_available_acts: 'Catalogue des actes',
  canvas_status: 'Lecture de l\'état du canvas',
  open_canvas: 'Ouverture du canvas',
  open_canvas_document: 'Ouverture d’un document',
  list_canvas_drafts: 'Lecture des brouillons',
  start_research_only_job: 'Préparation du plan de l\'acte',
  poll_research_job: 'Suivi de la rédaction',
  start_generation_only_job: 'Rédaction de l\'acte',
  enqueue_plan_detaille_render: 'Mise en forme du plan détaillé',
  list_user_writings: 'Liste de vos écritures',
  load_writing_into_canvas: 'Chargement de votre écriture',
  canvas_read: 'Lecture du document',
  canvas_search: 'Recherche dans le document',
  canvas_edit: 'Modification du document',
  canvas_format: 'Mise en forme du document',
  canvas_style: 'Application du style',
  canvas_comment: 'Gestion des commentaires',
  canvas_revisions: 'Suivi des modifications',
  canvas_mutations: 'Modification groupée',
  canvas_navigate: 'Navigation dans le document',
  // Canonical Canvas SuperDoc tools. `canvas_perform_action` is refined by
  // toolPresentation with the validated action catalogue when available.
  canvas_inspect: 'Lecture du Canvas',
  canvas_perform_action: 'Modification du Canvas',
  // Subagent invocation — fallback label utilisé par resolveBackendToolLabel()
  // quand agent_name est absent. Les labels dynamiques par agent vivent dans
  // components/common/toolPresentation.ts (SUBAGENT_LABELS) :
  //   - jurisprudence_analyzer → "Agent d'analyse de jurisprudence"
  //   - case_investigator      → "Agent d'exploration du dossier"
  //   - legal_research         → "Agent de recherche juridique"
  //   - general_purpose        → "Agent généraliste"
  invoke_subagent: 'Agent',
  // Legacy names (backend may still send these)
  search_legal_articles: "Recherche d'articles de loi",
  jurisprudence_search: 'Recherche de jurisprudence',
  case_documents_search: 'Recherche dans le dossier',
  legal_articles_search: "Recherche d'articles de loi",
  doctrine_search: 'Recherche de doctrine',
} as const;

export type BackendToolName = keyof typeof BACKEND_TOOL_LABELS;

// ============================================================================
// Backend Tool Icons (single source of truth)
// ============================================================================

export const BACKEND_TOOL_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  search_jurisprudence: Scale,
  search_case_documents: FileText,
  find_case_documents: FileSearch,
  search_doctrine: BookOpen,
  legifrance_search_code: Landmark,
  legifrance_search_legislation: Landmark,
  legifrance_get_article: Landmark,
  legifrance_list_codes: Landmark,
  legifrance_code_toc: Landmark,
  search_jp_by_id: Search,
  read_document: Book,
  split_document: Scissors,
  read_attached_document: Book,
  web_search: Globe,
  web_fetch: Globe,
  get_case_structure: FolderTree,
  get_case_wiki_overview: Microscope,
  read_case_wiki: Microscope,
  launch_case_wiki_analysis: Microscope,
  get_case_metadata: FolderTree,
  update_case_metadata: Edit3,
  set_piece_numbers: FileText,
  create_case_folders: FolderOpen,
  move_pieces: FolderOpen,
  copy_pieces: FolderOpen,
  rename_pieces: Edit3,
  rename_folders: Edit3,
  save_document: FolderOpen,
  save_canvas_to_case: FolderOpen,
  save_attachment_to_case: FolderOpen,
  compare_documents: FileText,
  read_document_format: FileText,
  search_cases: FolderOpen,
  add_related_case: FolderOpen,
  remove_related_case: FolderOpen,
  create_case: FolderPlus,
  load_skill: Sparkles,
  extract_legal_references: ScanSearch,
  context_compaction: Zap,
  open_objective: ListChecks,
  close_objective: ListChecks,
  read_memory: Brain,
  update_memory: Brain,
  list_case_conversations: MessagesSquare,
  get_conversation: MessageSquare,
  propose_ui_actions: ListChecks,
  invoke_subagent: Bot,
  // Connecteur courrier Microsoft (mailbox_*, lot C).
  mailbox_search_messages: Mail,
  mailbox_get_message: MailOpen,
  mailbox_read_attachment: Paperclip,
  mailbox_create_reply_draft: Reply,
  mailbox_update_reply_draft: Reply,
  // Rédaction web (canvas SuperDoc).
  list_available_acts: BookOpen,
  canvas_status: FileText,
  open_canvas: PanelRight,
  open_canvas_document: FilePlus2,
  list_canvas_drafts: Files,
  start_research_only_job: FileSearch,
  poll_research_job: FileSearch,
  start_generation_only_job: Edit3,
  enqueue_plan_detaille_render: Sparkles,
  list_user_writings: FolderOpen,
  load_writing_into_canvas: FileText,
  canvas_read: FileText,
  canvas_search: Search,
  canvas_edit: Edit3,
  canvas_format: Type,
  canvas_style: Type,
  canvas_comment: MessageSquarePlus,
  canvas_revisions: Highlighter,
  canvas_mutations: Sparkles,
  canvas_navigate: Navigation,
  canvas_inspect: FileSearch,
  canvas_perform_action: Edit3,
  // Word action tools — icônes alignées sur la SSOT add-in (wordActionLabels.ts).
  get_all_paragraphs: FileText,
  get_context_range: FileText,
  get_selection: FileSearch,
  get_document_structure: FileSearch,
  get_paragraph_format: Type,
  search_replace: Edit3,
  insert_paragraph: Edit3,
  delete_paragraph: Edit3,
  apply_text_format: Type,
  apply_font: Type,
  clear_formatting: Type,
  set_alignment: Type,
  set_paragraph_style: Type,
  setup_hector_styles: Type,
  highlight_range: Highlighter,
  navigate_to_paragraph: Navigation,
  insert_comment: MessageSquarePlus,
  // Legacy names
  search_legal_articles: Landmark,
  jurisprudence_search: Scale,
  case_documents_search: FileText,
  legal_articles_search: Landmark,
  doctrine_search: BookOpen,
};

/**
 * Get a human-readable label for a backend tool
 */
export function getBackendToolLabel(toolName: string): string {
  // Historical Canvas events may contain an old or unknown tool name. Keep the
  // timeline understandable without ever surfacing an implementation identifier.
  if (toolName.startsWith('canvas_')) {
    return BACKEND_TOOL_LABELS[toolName] || 'Action dans le Canvas';
  }
  return BACKEND_TOOL_LABELS[toolName] || toolName;
}
